import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import API, { BACKEND_URL } from "../api/api";
import { documentPaths } from "../utils/documents";

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleString("nl-BE");
};

const parsePhotos = (value) => {
  try {
    const parsed = typeof value === "string" ? JSON.parse(value || "[]") : value;
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch {
    return [];
  }
};

export default function Archive() {
  const [activeTab, setActiveTab] = useState("customers");
  const [search, setSearch] = useState("");
  const queryClient = useQueryClient();

  const { data: customers = [], isLoading: customersLoading, isError: customersError } = useQuery({
    queryKey: ["customers", "archive"],
    queryFn: async () => (await API.get("/customers?includeArchived=true")).data
  });

  const { data: damageCases = [], isLoading: damageLoading, isError: damageError } = useQuery({
    queryKey: ["damage-cases", "archive"],
    queryFn: async () => (await API.get("/damage-cases?includeArchived=true")).data
  });

  const archivedCustomers = useMemo(() => {
    const term = search.trim().toLowerCase();
    return customers
      .filter((customer) => customer.archivedAt)
      .filter((customer) => {
        if (!term) return true;
        return (
          `${customer.firstName || ""} ${customer.lastName || ""}`.toLowerCase().includes(term) ||
          (customer.phone || "").toLowerCase().includes(term) ||
          (customer.email || "").toLowerCase().includes(term)
        );
      });
  }, [customers, search]);

  const archivedDamageCases = useMemo(() => {
    const term = search.trim().toLowerCase();
    return damageCases
      .filter((damageCase) => damageCase.archivedAt)
      .filter((damageCase) => {
        if (!term) return true;
        return (
          (damageCase.customerName || "").toLowerCase().includes(term) ||
          (damageCase.customerPhone || "").toLowerCase().includes(term) ||
          (damageCase.trailer?.name || "").toLowerCase().includes(term)
        );
      });
  }, [damageCases, search]);

  const isLoading = customersLoading || damageLoading;
  const isError = customersError || damageError;
  const deleteCustomerDocumentMutation = useMutation({
    mutationFn: ({ id, path }) => API.delete(`/customers/${id}/documents`, { data: { path } }),
    onSuccess: () => {
      toast.success("Archiefdocument verwijderd");
      queryClient.invalidateQueries({ queryKey: ["customers", "archive"] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || "Archiefdocument verwijderen mislukt");
    }
  });
  const deleteDamagePhotoMutation = useMutation({
    mutationFn: ({ id, path }) => API.delete(`/damage-cases/${id}/photos`, { data: { path } }),
    onSuccess: () => {
      toast.success("Archieffoto verwijderd");
      queryClient.invalidateQueries({ queryKey: ["damage-cases", "archive"] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || "Archieffoto verwijderen mislukt");
    }
  });

  const deleteCustomerDocument = (id, path) => {
    if (window.confirm("Dit archiefdocument permanent verwijderen?")) {
      deleteCustomerDocumentMutation.mutate({ id, path });
    }
  };

  const deleteDamagePhoto = (id, path) => {
    if (window.confirm("Dit archiefdocument permanent verwijderen?")) {
      deleteDamagePhotoMutation.mutate({ id, path });
    }
  };

  if (isLoading) return <div className="loading-state">Archief laden...</div>;
  if (isError) return <div className="error-state">Archief laden mislukt</div>;

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Archief</h1>
          <p className="page-subtitle">Bekijk gearchiveerde klanten en schadegevallen.</p>
        </div>
      </header>

      <div className="panel panel-pad">
        <div className="archive-toolbar">
          <div className="segmented-control">
            <button
              className={activeTab === "customers" ? "active" : ""}
              onClick={() => setActiveTab("customers")}
            >
              Klanten ({archivedCustomers.length})
            </button>
            <button
              className={activeTab === "damage" ? "active" : ""}
              onClick={() => setActiveTab("damage")}
            >
              Schadegevallen ({archivedDamageCases.length})
            </button>
          </div>
          <input
            className="input archive-search"
            placeholder="Zoek in archief"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {activeTab === "customers" ? (
        <div className="list">
          {archivedCustomers.length === 0 && (
            <div className="panel empty-state">Geen gearchiveerde klanten gevonden.</div>
          )}
          {archivedCustomers.map((customer) => (
            <div key={customer.id} className="list-row archive-row">
              <div>
                <div className="row-title">{customer.firstName} {customer.lastName}</div>
                <div className="row-meta">{customer.phone || "-"} - {customer.email || "-"}</div>
                <div className="status-line">
                  <span className="badge">Gearchiveerd op {formatDate(customer.archivedAt)}</span>
                  {customer.wantsInvoice && <span className="badge warning">Factuur {customer.vatNumber || "-"}</span>}
                </div>
                {documentPaths(customer.documents).length > 0 && (
                  <div className="photo-grid">
                    {documentPaths(customer.documents).map((path) => (
                      <div key={path} className="photo-thumb-wrap">
                        <a className="checkout-admin-photo" href={`${BACKEND_URL}${path}`} target="_blank" rel="noreferrer">
                          <img className="photo-thumb" src={`${BACKEND_URL}${path}`} alt="Archiefdocument" />
                          <span>Document</span>
                        </a>
                        <button
                          type="button"
                          className="photo-delete-button"
                          onClick={() => deleteCustomerDocument(customer.id, path)}
                          aria-label="Archiefdocument verwijderen"
                        >
                          x
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <Link className="button secondary" to={`/customers/${customer.id}?archived=true`}>
                Bekijken
              </Link>
            </div>
          ))}
        </div>
      ) : (
        <div className="list">
          {archivedDamageCases.length === 0 && (
            <div className="panel empty-state">Geen gearchiveerde schadegevallen gevonden.</div>
          )}
          {archivedDamageCases.map((damageCase) => (
            <div key={damageCase.id} className="list-row archive-row">
              <div>
                <div className="row-title">{damageCase.customerName}</div>
                <div className="row-meta">{damageCase.customerPhone || "-"} - {damageCase.trailer?.name || "Geen trailer"}</div>
                <div className="status-line">
                  <span className="badge">Gearchiveerd op {formatDate(damageCase.archivedAt)}</span>
                  <span className="badge info">{damageCase.status}</span>
                  <span className="badge danger">EUR {damageCase.damageCost || 0}</span>
                </div>
                {damageCase.description && <p className="page-subtitle">{damageCase.description}</p>}
                {parsePhotos(damageCase.photos).length > 0 && (
                  <div className="photo-grid">
                    {parsePhotos(damageCase.photos).map((path) => (
                      <div key={path} className="photo-thumb-wrap">
                        <a className="checkout-admin-photo" href={`${BACKEND_URL}${path}`} target="_blank" rel="noreferrer">
                          <img className="photo-thumb" src={`${BACKEND_URL}${path}`} alt="Archieffoto schadegeval" />
                          <span>Foto</span>
                        </a>
                        <button
                          type="button"
                          className="photo-delete-button"
                          onClick={() => deleteDamagePhoto(damageCase.id, path)}
                          aria-label="Archieffoto verwijderen"
                        >
                          x
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
